import { cva } from "class-variance-authority";

// Pressed = hover (spec), so no variant carries active: styles.
const commonButtonStyles =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/80 disabled:opacity-50 disabled:pointer-events-none aria-busy:pointer-events-none aria-busy:opacity-50 transition-colors motion-reduce:transition-none [:where(&)_svg:where([width='24'][stroke='currentColor'],[width='1em'],:not([width]))]:size-4 [&_svg]:shrink-0";

// Applied to the wrapper div around <input>, so focus and invalid states are
// read from the inner <input> via has-[...], never from slot buttons.
const commonInputStyles =
  "flex font-normal rounded outline-none border border-input bg-background text-foreground has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-ring/20 has-[input:focus-visible]:border-ring has-[input[aria-invalid=true]]:border-destructive has-[input[aria-invalid=true]:focus-visible]:ring-destructive/20 dark:has-[input[aria-invalid=true]:focus-visible]:ring-destructive/40 has-[input[aria-invalid=true]:focus-visible]:border-destructive file:border-0 file:bg-transparent file:text-sm has-[input:disabled]:opacity-50";

// Menu items (dropdown, context, command) opt in with `variant="destructive"`; the
// destructive pair replaces the accent hover so delete never reads as select.
export const menuItemDestructiveClasses =
  "data-[variant=destructive]:text-destructive-text data-[variant=destructive]:focus:bg-destructive-muted data-[variant=destructive]:focus:text-destructive-muted-foreground data-[variant=destructive]:aria-selected:bg-destructive-muted data-[variant=destructive]:aria-selected:text-destructive-muted-foreground";

// Shared layout and type of a menu or list row (DropdownMenuItem, CommandItem,
// hand-built picker rows): 14px regular text, min 32px tall, 8px/6px padding.
export const menuRowClass =
  "relative flex cursor-pointer select-none items-center gap-2 min-h-8 rounded px-2 py-1.5 text-sm text-popover-foreground";

// The muted heading above a block of menu rows (DropdownMenuLabel, ContextMenuLabel, popover menus).
export const menuLabelClass = "px-2 py-1.5 text-xs font-normal text-muted-foreground";

// Standard 16px icon for a menu row; CommandItem deliberately omits it (20px entity icons).
export const menuRowIconClass = "[&_svg]:size-4 [&_svg]:shrink-0";

// Every Radix menu and select row (item, checkbox, radio, sub-trigger): the row, 16px
// icons and the accent highlight. Checkbox, radio and select rows add `pr-8` for the
// indicator, which sits at `absolute right-2`.
export const menuItemClass = `${menuRowClass} ${menuRowIconClass} outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none`;

export const menuSeparatorClass = "-mx-1 my-1 h-px bg-border";

// A full-width list row: Button size="row" and StaticRow share it.
export const rowGeometryClass = "h-8 w-full justify-start px-1 mobile:h-11";

// A form field's dropdown trigger (SelectTrigger, a field ComboBox): the same 36px box beside inputs.
export const fieldTriggerClass =
  "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-[8px] border border-input bg-background text-foreground px-3 py-2 text-xs font-normal placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive/20 dark:aria-[invalid=true]:focus-visible:ring-destructive/40 aria-[invalid=true]:focus-visible:border-destructive disabled:cursor-not-allowed disabled:opacity-50";

// Every menu surface (dropdown, context, select, their submenus) starts this wide.
export const menuMinWidthClass = "min-w-[12rem]";

// A selector's popover or submenu (ComboBox): no padding and no scroll of
// its own, so the search row stays put and only the CommandList scrolls.
export const selectorPanelClass = "p-0 overflow-hidden mobile:!overflow-hidden";

// The one popup surface: menus, context menus, selects, pickers, popovers and hover
// cards share the border, fill, radius, shadow and motion. Padding and height stay
// per kind. `exit: false` is for Select, which must close instantly (see select.tsx).
export const popupSurfaceVariants = cva(
  "z-floating rounded-md border border-border bg-popover text-popover-foreground shadow-xl outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  {
    variants: {
      exit: {
        true: "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        false: ""
      }
    },
    defaultVariants: { exit: true }
  }
);

// Multi-select indicator for every menu and picker; single-select shows a bare check.
export const menuCheckboxVariants = cva(
  "flex size-4 shrink-0 items-center justify-center rounded border border-card",
  { variants: { checked: { true: "bg-card", false: "opacity-50" } } }
);

// Exactly the design spec's nine variants (docs/design-system/brev-colour-system.md,
// "Button"), plus two composed only from existing spec rules: `quiet` is the
// sidebar item's muted-rest/accent-hover pair, `destructive-ghost` rests the same
// and hovers to the destructive menu item's pair (a bare dismiss/delete icon),
// `primary-outline` is outline with the applied filter pill's `border-primary` stroke,
// `current` is quiet's hover surface on whatever text colour surrounds it.
// Pressed = hover (spec), and a menu trigger's open state counts as pressed.
// Colour belongs here, never in a caller's className (check:button-colours).
export const buttonVariants = cva(commonButtonStyles, {
  variants: {
    variant: {
      default: "bg-primary text-primary-foreground hover:bg-primary-hover",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
      outline:
        "border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground aria-pressed:bg-accent aria-pressed:text-accent-foreground data-[selected=true]:bg-accent focus-visible:border-ring aria-[invalid=true]:border-destructive",
      "primary-outline":
        "border border-primary bg-background text-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
      ghost: "text-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
      quiet: "text-muted-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
      link: "text-primary underline-offset-4 hover:underline",
      // A title picker: a heading that opens a menu, with no box. Text only, it dims on hover and while open; pair with size="inline" inside a SectionTitle for the type size.
      title: "font-medium text-foreground hover:text-muted-foreground data-[state=open]:text-muted-foreground",
      destructive:
        "bg-destructive text-destructive-foreground hover:bg-destructive-hover focus-visible:ring-destructive/80",
      "destructive-outline":
        "border border-border bg-background text-destructive-text hover:bg-destructive-muted hover:text-destructive-muted-foreground focus-visible:ring-destructive/80 focus-visible:border-destructive",
      "destructive-muted":
        "bg-destructive-muted text-destructive-muted-foreground hover:bg-destructive-muted-hover focus-visible:ring-destructive/80",
      // Ghost inside the nav sidebars: the sidebar-accent family, which differs from accent in dark mode (BRE-4674).
      sidebar:
        "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
      "destructive-ghost":
        "text-muted-foreground hover:bg-destructive-muted hover:text-destructive-muted-foreground focus-visible:ring-destructive/80",
      // A link inside a notice or strip: takes the surrounding text colour, underlined. Pair with size="inline".
      inline: "underline",
      // An icon button inside a tinted notice (a callout's icon): keeps the notice's text colour, quiet's hover surface.
      current: "hover:bg-accent data-[state=open]:bg-accent",
      // An action on a warning strip or pill: the muted warning pair, solid warning on hover.
      "warning-muted": "bg-warning-muted text-warning-muted-foreground hover:bg-warning hover:text-warning-foreground",
      // Floats over media (a video frame): a dark scrim with light ink in both themes.
      scrim: "bg-black/50 text-white hover:bg-black/70",
      // A form field's trigger (a date picker, a custom field): SelectTrigger's look, chevron included (Button draws it); its geometry is the compound below.
      field: "",
      // A filter pill: the outline look with 14px icons; data-active="true" is the applied (green) state.
      filter:
        "border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground focus-visible:border-ring overflow-hidden data-[active=true]:bg-accent data-[active=true]:border-primary data-[active=true]:text-accent-foreground [&_svg]:size-3.5"
    },
    size: {
      default: "h-9 px-4",
      sm: "h-8 px-3 text-xs",
      xsm: "h-7 px-3 text-xs",
      // The design system's 24px "xx-small" (BRE-8133).
      xxs: "h-6 px-2 text-xs gap-1 [:where(&)_svg:not(g):where([width='24'][stroke='currentColor'],[width='1em'],:not([width]))]:size-3.5",
      lg: "h-10 px-8",
      // 44px, the mobile touch target, for a full-width text action (a mobile sheet footer).
      touch: "h-11 px-4",
      icon: "h-9 w-9 shrink-0",
      // Icon-only squares under the text sizes: 32px (sm), 28px (xsm), 24px (a row's status glyph), 20px; never shrink in a flex row.
      "icon-sm": "h-8 w-8 shrink-0",
      "icon-xsm": "h-7 w-7 shrink-0",
      "icon-xs": "h-6 w-6 shrink-0",
      // 44px, the mobile touch target, with a 24px icon (the mobile top bar).
      "icon-lg": "h-11 w-11 shrink-0 [:where(&)_svg:not(g):where([width='24'][stroke='currentColor'],[width='1em'],:not([width]))]:size-6",
      "icon-2xs": "h-5 w-5 shrink-0 [:where(&)_svg:not(g):where([width='24'][stroke='currentColor'],[width='1em'],:not([width]))]:size-3.5",
      noPadding: "h-7",
      // A full-width row in a list (a hover card's links, a dialog's "Add" row): 32px, 44px on mobile, a 4px inset.
      row: rowGeometryClass,
      // Sits in running text: no box of its own, the text's size and line height; a long label wraps instead of overflowing.
      inline: "h-auto p-0 text-[length:inherit] whitespace-normal text-left",
      // A 20px pill with 12px text and icon, for a compact action inside a pill or strip; grows if its content wraps.
      pill: "min-h-5 rounded-full px-2.5 text-xs font-medium gap-1 [:where(&)_svg:not(g):where([width='24'][stroke='currentColor'],[width='1em'],:not([width]))]:size-3",
      // A multi-line choice tile (a suggested task, a numbered answer option): wraps, content top-aligned.
      card: "h-auto items-start justify-start gap-2 whitespace-normal rounded-lg p-3 text-left",
      // A rounded suggestion pill whose label wraps in a narrow rail: 28px for one line, grows for more.
      suggestion: "h-auto min-h-7 justify-start whitespace-normal rounded-full px-3 py-1.5 text-left text-xs"
    }
  },
  // The field look wins over any size: a form field is always the 36px field box.
  // The design system's Link button at xsm: a 4px inset and medium weight, so its text sits nearly flush with the copy above it.
  compoundVariants: [
    { variant: "field", class: fieldTriggerClass },
    { variant: "link", size: "xsm", class: "px-1 font-medium" },
  ],
  defaultVariants: { variant: "default", size: "default" }
});

// One look for every inline property control in a row (tags, due date, goal,
// meeting, status, type), whatever element renders it. Copied from the OKR tree
// row, the reference: no surface, regular-weight label, 16px icon, and hover or
// an open popover lifts the text from muted to foreground.
export const propertyTriggerVariants = cva(
  "inline-flex items-center gap-1 rounded-md bg-transparent [box-shadow:none] text-sm font-normal text-muted-foreground transition-colors hover:bg-transparent hover:text-foreground data-[state=open]:bg-transparent data-[state=open]:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/80 disabled:pointer-events-none disabled:opacity-50 [:where(&)_svg:where([width='24'][stroke='currentColor'],[width='1em'],:not([width]))]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      size: {
        // 28px: the OKR row control's height, pinned so a host Button size cannot override it.
        row: "h-7 py-0 px-0.5 border-0",
        chip: "h-7 px-2 py-0 border border-border",
        sheet: "min-h-9 px-2 py-1 border-0"
      },
      // The label's type: `xs` is the 12px medium label of metadata lines, chips and table cells.
      text: {
        default: "",
        xs: "text-xs font-medium"
      },
      // A property whose value is a warning state (an excluded item) shows it in the trigger's ink.
      tone: {
        default: "",
        destructive:
          "text-destructive-text hover:text-destructive-text data-[state=open]:text-destructive-text [&>svg]:text-destructive",
        // Due today, and the like: needs attention soon.
        warning: "text-warning hover:text-warning data-[state=open]:text-warning"
      }
    },
    defaultVariants: { size: "row", text: "default", tone: "default" }
  }
);

/** A row control hidden until its row (the nearest `group`) is hovered or holds keyboard
 * focus, or it is focused or open itself; always shown on touch, which has no hover. Not
 * `group-focus-within`: a mouse-closed picker leaves focus on its trigger, which kept the
 * control showing on a row the pointer had left (BRE-9299). */
export const revealOnHoverClass =
  "opacity-0 transition-opacity group-hover:opacity-100 group-has-[:focus-visible]:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 mobile:opacity-100";

/** The keyboard-active row in a popup list (command palette, @ mention and : emoji
 * suggestions): accent, like a highlighted menu item. Mark the row aria-selected. */
export const popupRowActiveClass = "outline-none aria-selected:bg-accent aria-selected:text-accent-foreground";

/** A row inside a card or popover, flush with its padding (a hover card's person rows): ClickableRow `inset`. */
export const insetRowClass = "block w-full rounded-md p-1";

/** The spec's clickable row or tile (goal tree, team rows, list items): accent on
 * hover and keyboard focus, muted when selected. For a whole-row click target that
 * cannot be a Button because it lays out multi-line content. */
export const clickableRowClass =
  "transition-colors not-dragging:hover:bg-accent not-dragging:hover:text-accent-foreground focus-visible:outline-none focus-visible:bg-accent focus-visible:text-accent-foreground data-[state=selected]:bg-muted aria-selected:bg-muted disabled:pointer-events-none disabled:opacity-50";

/** An empty property rests faded and comes back to full strength when engaged. */
export const PROPERTY_TRIGGER_EMPTY_CLASS =
  "opacity-50 hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100";

// The spec's Toggle: a two-state control. Accent means "on", so its hover is muted.
export const toggleVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-normal transition-colors motion-reduce:transition-none hover:bg-muted hover:text-muted-foreground aria-pressed:bg-accent aria-pressed:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/80 disabled:pointer-events-none disabled:opacity-50 [:where(&)_svg:where([width='24'][stroke='currentColor'],[width='1em'],:not([width]))]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        // Pressed matches the tabs' selected state (TabsContainer, SegmentedTabs):
        // accent fill with a primary edge.
        outline: "border border-input bg-background aria-pressed:border-primary"
      },
      size: {
        default: "h-9 px-2.5",
        sm: "h-8 px-2 text-xs",
        xsm: "h-7 px-2 text-xs",
        // 24px, as Button's xxs: a reaction or count chip.
        xxs: "h-6 px-2 text-xs gap-1",
        // A channel chip in a settings row (BRE-9718): 32px, 44px on mobile, as rowGeometryClass.
        chip: "h-8 px-2.5 text-xs gap-1 mobile:h-11",
        // Icon-only, as Button's icon-xsm (28px): a formatting toolbar.
        "icon-sm": "h-8 w-8 shrink-0",
        "icon-xsm": "h-7 w-7 shrink-0",
        // A tile whose content stacks and wraps (SelectCard, a two-line choice): grows with it.
        card: "h-auto flex-col items-start justify-start gap-2 whitespace-normal rounded-md p-3 text-left"
      }
    },
    defaultVariants: { variant: "default", size: "default" }
  }
);

// A keyboard key cap. `default` is the Badge spec's 20px with 12px text, for a
// key in body text or beside a control; `sm` is the 16px cap inside a control;
// `xs` is the 16px cap with 10px text, for a key legend in a footer.
export const kbdVariants = cva(
  "inline-flex items-center justify-center rounded border border-border bg-muted font-sans font-normal leading-none text-muted-foreground",
  {
    variants: {
      size: {
        default: "h-5 min-w-5 px-1.5 text-xs",
        sm: "h-4 min-w-4 px-1 text-xs",
        xs: "h-4 min-w-4 px-1.5 text-[10px] font-medium"
      }
    },
    defaultVariants: { size: "default" }
  }
);

// Code text: `inline` sits in a sentence at 0.875em of the text around it;
// `block` is a scrolling multi-line block (a <pre>).
export const codeVariants = cva(
  "rounded border border-border bg-muted font-mono text-foreground",
  {
    variants: {
      variant: {
        inline: "px-1 py-0.5 text-[0.875em]",
        block: "block overflow-x-auto whitespace-pre rounded-md p-3 text-sm leading-relaxed"
      }
    },
    defaultVariants: { variant: "inline" }
  }
);

// A glyph centred on a rounded tile: a Lucide icon ahead of a title (the docs
// category cards and article heroes), an app glyph, or a brand logo (`card`).
// row, xsm, sm, default, lg, xl and 2xl size a Lucide icon; the smaller and logo
// sizes hold a glyph that sets its own size.
export const iconTileVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-lg [&_svg]:shrink-0",
  {
    variants: {
      tone: {
        muted: "bg-muted text-foreground",
        primary: "bg-primary/10 text-primary",
        accent: "bg-accent text-accent-foreground",
        // No fill: a glyph that only takes a tile when its state needs one.
        bare: "",
        outline: "border border-border",
        background: "bg-background text-foreground",
        // A colour-on-white brand logo, white in both themes.
        card: "border border-border bg-card shadow-sm",
        success: "bg-success-muted text-success",
        warning: "bg-warning-muted text-warning",
        destructive: "bg-destructive-muted text-destructive-muted-foreground",
        highlight: "bg-highlight-muted text-highlight-muted-foreground",
        // A muted glyph on the muted fill: a pending or empty state.
        subtle: "bg-muted text-muted-foreground",
        // Solid fills: a selected marker, a done check, a live mark, a brand mark.
        "primary-solid": "bg-primary text-primary-foreground",
        "success-solid": "bg-success text-primary-foreground",
        "destructive-solid": "bg-destructive text-destructive-foreground",
        "highlight-solid": "bg-highlight text-highlight-foreground",
        inverse: "bg-foreground text-background"
      },
      size: {
        // One of two sharing a 20px tile on a diagonal, ringed so the overlap reads as two (BRE-9913).
        stacked: "size-[13px] rounded-sm text-[10px] font-medium leading-none ring-1 ring-background",
        "2xs": "size-4 rounded",
        xs: "size-5 rounded-md",
        // A list row's 24px status marker, level with the row's 24px controls.
        row: "size-6 rounded-md [&_svg]:size-3",
        xsm: "size-7 rounded-md [&_svg]:size-4",
        sm: "size-8 [&_svg]:size-4",
        default: "size-9 [&_svg]:size-[18px]",
        lg: "size-11 [&_svg]:size-5",
        xl: "size-12 [&_svg]:size-6",
        // The glyph that heads a not-found or error page.
        "2xl": "size-16 [&_svg]:size-8",
        // A logo over a cover strip (the Quick Start sidebar card, BRE-9621): 36px, ringed in the card colour.
        "logo-strip": "size-9 overflow-hidden rounded-[9px] border-2 border-card",
        "logo-sm": "size-8 rounded-md",
        logo: "size-10",
        "logo-lg": "size-12 rounded-xl",
        "logo-wide": "h-8 w-12 rounded",
        // A logo overhanging a cover image (Quick Start, BRE-9616): ringed in the page colour, 64px on phones, 88px from md.
        cover: "size-16 overflow-hidden rounded-2xl border-4 border-background md:size-[88px] md:rounded-[20px]"
      },
      shape: {
        circle: "rounded-full"
      }
    },
    defaultVariants: { tone: "muted", size: "default" }
  }
);


export const textareaVariants = cva(
  "flex w-full rounded-[8px] text-foreground py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none aria-[invalid=true]:border-destructive disabled:cursor-not-allowed disabled:opacity-50 resize-none",
  {
    variants: {
      // Short fields hide the scrollbar; a fixed-height editor for long text shows it.
      scrollbar: {
        hidden: "no-scrollbar",
        visible: ""
      },
      variant: {
        default:
          "border border-input bg-background px-3 focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring aria-[invalid=true]:focus-visible:ring-destructive/20 dark:aria-[invalid=true]:focus-visible:ring-destructive/40 aria-[invalid=true]:focus-visible:border-destructive",
        // Same contract as the ghost input: no border, ring or fill; it shows the surface it sits on.
        ghost: "border-0 bg-transparent rounded-none",
        // A source editor (JSON, Mermaid): 12px monospace on a faint muted surface, resizable.
        code: "rounded-md border border-border bg-muted/20 p-2 font-mono text-xs resize-y focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring",
        // A composer line that reads as a muted placeholder row until committed: no frame or fill.
        draft: "border-0 bg-transparent py-0 rounded-none text-muted-foreground placeholder:italic"
      }
    },
    defaultVariants: { variant: "default", scrollbar: "hidden" }
  }
);

export const inputVariants = cva(commonInputStyles, {
  variants: {
    variant: {
      default: "bg-background",
      title: "px-0 font-bold w-full",
      destructive: "bg-destructive text-destructive-foreground",
      profile: "bg-background text-xs py-1.5 border w-[12rem]",
      login: "bg-secondary px-2 h-10",
      // No border, ring or fill in any state: a filled ghost field paints square
      // corners over a rounded container and reads as a well on other surfaces.
      ghost:
        "bg-transparent py-1 rounded-none border-l-2 border-transparent has-[input:focus-visible]:ring-0 has-[input:focus-visible]:border-transparent",
      // For an input nested inside a control that draws its own frame, fill and
      // focus ring (SearchInput): the input itself paints nothing.
      bare:
        "bg-transparent py-1 rounded-none border-transparent has-[input:focus-visible]:ring-0 has-[input:focus-visible]:border-transparent",
      // A text field that reads as plain text in its row until edited (inline titles,
      // creation rows): no frame, fill, padding or ring; line height follows fontSize.
      inline:
        "bg-transparent py-0 rounded-none border-0 has-[input:focus-visible]:ring-0",
      outline: "bg-transparent py-1 rounded-md border",
      // In-place text editing (titles and names): inline with an underline that shows on
      // hover and takes the ring colour on focus. Form fields keep the default border.
      // -mb-px: the underline must not add height, or the content below shifts while editing.
      underline:
        "-mb-px bg-transparent py-0 rounded-none border-x-0 border-t-0 border-b-transparent hover:border-b-foreground/20 has-[input:focus-visible]:border-b-ring has-[input:focus-visible]:ring-0",
      onboarding:
        "w-full h-9 border px-3 py-1 rounded-[4px] text-foreground [&_input::placeholder]:text-muted-foreground"
    },
    fontSize: {
      default: "text-[1rem]", // 16px
      xs: "text-[0.75rem]", // 12px
      sm: "text-[0.875rem]", // 14px
      lg: "text-[1.125rem]", // 18px
      xl: "text-[1.25rem]", // 20px
      // SectionTitle size="2xl", so a page title edits in place at its own size (BRE-9792).
      "2xl": "text-2xl", // 24px
      title: "text-[2.5rem]" // 40px
    },
    // Field heights match the Button sizes, so a field sits level with the button
    // beside it. Omitting size keeps the field's content height.
    size: {
      default: "h-9",
      sm: "h-8",
      xsm: "h-7",
      xs: "h-6"
    },
    hasError: {
      true: "border-destructive has-[input:focus-visible]:border-destructive has-[input:focus-visible]:ring-destructive/20 dark:has-[input:focus-visible]:ring-destructive/40",
      false: ""
    }
  },
  compoundVariants: [
    { variant: ["inline", "underline"], fontSize: "xs", class: "leading-4" },
    { variant: ["inline", "underline"], fontSize: "sm", class: "leading-5" },
    { variant: ["inline", "underline"], fontSize: "default", class: "leading-6" },
    { variant: ["inline", "underline"], fontSize: ["lg", "xl"], class: "leading-7" },
    { variant: ["inline", "underline"], fontSize: "2xl", class: "leading-8" }
  ],
  defaultVariants: {
    variant: "default",
    fontSize: "default",
    hasError: false
  }
});

/**
 * The Operator's floating bottom pill over arbitrary page content (BRE-5817).
 * Type and ground stay identical in every state: rest reads as quiet through width,
 * shadow and a softer edge, never smaller or faded text. Hover or focus promotes a
 * resting pill to the full affordance; a stacked pill stays put so the pair reads as one.
 */
export const floatingPillVariants = cva(
  "group pointer-events-auto relative flex items-center overflow-hidden rounded-2xl bg-card text-left px-4 py-3 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/80",
  {
    variants: {
      size: {
        prominent: "w-[min(400px,100%)] border border-border shadow-xl",
        stacked: "max-w-[min(340px,100%)] border border-border/40 shadow-md",
        resting:
          "max-w-[min(340px,100%)] border border-border/40 shadow-md hover:max-w-[min(560px,100%)] hover:border-border hover:shadow-xl focus-visible:max-w-[min(560px,100%)] focus-visible:border-border focus-visible:shadow-xl"
      }
    },
    defaultVariants: { size: "resting" }
  }
);

/** The page surface. Opaque page headers (sticky or layered) use this same class,
 * never `bg-card`, so in dark mode the header cannot read as a separate band. */
export const PAGE_SURFACE_CLASS = "bg-background";

/** The scroller every primary page puts its content in. Page scrollbars are
 * hidden app-wide; scrolling by wheel, touch and keyboard is unaffected. */
export const PAGE_SCROLL_CLASS = "overflow-y-auto no-scrollbar";
